// TRIAGE-01 (IMP-01) — the per-image finding cursor behind `j`/`k`.
//
// Orders the ACTIVE image's effective findings (the same
// `useEffectiveFindingSet` narrowing the List/Tiles/Viewer share —
// AI-class/confidence filter applied, no state narrowing) as:
//
//     pending first, then the rest — each group in query (List) order.
//
// and exposes relative movement that writes through the shared
// selection store (`selectOne` + `activeKind: "ai"`), so the List row
// highlight + the Viewer bbox highlight follow for free (R58-L-13).
//
// Movement clamps at both ends (no wrap — consistent with the
// data-point navigation, ANA-R3-NAV-01 OQ2). When nothing is selected
// (or the selection is not in the ordered set, e.g. a defect), `next`/
// `previous` select the FIRST entry.
import { useCallback, useMemo } from "react";
import type { AiFinding } from "@aeriosiq/types";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { useSelectionStore } from "../state/selectionStore.js";
import { useEffectiveFindingSet } from "../useEffectiveFindingSet.js";

export interface PendingFindingCursor {
  /** Ordered ids — pending first, then others, query order per group. */
  orderedIds: readonly string[];
  /** Ordered PENDING finding ids on the active image. */
  pendingIds: readonly string[];
  /** The active image's effective findings (unordered, query order). */
  findings: readonly AiFinding[];
  /** Select the next finding in cursor order (clamps at the end). */
  selectNext: () => boolean;
  /** Select the previous finding in cursor order (clamps at the start). */
  selectPrevious: () => boolean;
  /** Select the first PENDING finding; false when none is pending. */
  selectFirstPending: () => boolean;
  /**
   * Auto-advance helper (IMP-01 / Q2): select the next pending finding
   * EXCLUDING `decidedId` (the row just promoted/dismissed — its cached
   * status may still read `pending` until the optimistic flip lands).
   * Returns false when no other pending finding remains on the image.
   */
  selectNextPendingAfter: (decidedId: string) => boolean;
}

/** Pure ordering helper — exported for unit tests. */
export function orderFindingsForCursor(
  findings: readonly AiFinding[],
): string[] {
  const pending: string[] = [];
  const rest: string[] = [];
  for (const f of findings) {
    (f.status === "pending" ? pending : rest).push(f.id);
  }
  return [...pending, ...rest];
}

export function usePendingFindingCursor(
  dataAcquisitionId: string,
): PendingFindingCursor {
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const { findings } = useEffectiveFindingSet(dataAcquisitionId, {
    imageId: activeImageId,
  });

  const orderedIds = useMemo(
    () => orderFindingsForCursor(findings),
    [findings],
  );
  const pendingIds = useMemo(
    () => findings.filter((f) => f.status === "pending").map((f) => f.id),
    [findings],
  );

  const selectId = useCallback((id: string): boolean => {
    useSelectionStore.getState().selectOne(id, "ai");
    return true;
  }, []);

  const moveBy = useCallback(
    (offset: 1 | -1): boolean => {
      if (orderedIds.length === 0) return false;
      const { activeFindingId } = useSelectionStore.getState();
      const idx =
        activeFindingId === null ? -1 : orderedIds.indexOf(activeFindingId);
      if (idx === -1) {
        const first = orderedIds[0];
        return first !== undefined ? selectId(first) : false;
      }
      const target = orderedIds[idx + offset];
      if (target === undefined) return false; // clamp — no wrap
      return selectId(target);
    },
    [orderedIds, selectId],
  );

  const selectNext = useCallback(() => moveBy(1), [moveBy]);
  const selectPrevious = useCallback(() => moveBy(-1), [moveBy]);

  const selectFirstPending = useCallback((): boolean => {
    const first = pendingIds[0];
    if (first === undefined) return false;
    return selectId(first);
  }, [pendingIds, selectId]);

  const selectNextPendingAfter = useCallback(
    (decidedId: string): boolean => {
      const remaining = pendingIds.filter((id) => id !== decidedId);
      if (remaining.length === 0) return false;
      // Prefer the next pending AFTER the decided row's position so the
      // cursor flows downward; fall back to the first remaining one.
      const decidedIdx = pendingIds.indexOf(decidedId);
      const after =
        decidedIdx === -1
          ? undefined
          : pendingIds.slice(decidedIdx + 1).find((id) => id !== decidedId);
      const target = after ?? remaining[0];
      return target !== undefined ? selectId(target) : false;
    },
    [pendingIds, selectId],
  );

  return {
    orderedIds,
    pendingIds,
    findings,
    selectNext,
    selectPrevious,
    selectFirstPending,
    selectNextPendingAfter,
  };
}
